import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import {
  UserAreaFileModel,
  UserAreaLinkModel,
  UserAreaMembershipModel,
  UserAreaModel,
  UserAreaRole,
} from '../../../../models/user-area.model';
import { AlertService } from '../../../../services/alert.service';
import { UserAreaService } from '../../../../services/user-area.service';
import { UserService } from '../../../../services/user.service';
import { ConfirmDialogComponent } from '../../../shared-components/confirm-dialog/confirm-dialog.component';
import { EditMemberAreaComponent } from '../../../shared-components/edit-member-area/edit-member-area.component';
import { InviteMemberAreaComponent } from '../../../shared-components/invite-member-area/invite-member-area.component';
import { MemberAreaContactDialogComponent } from '../../../shared-components/member-area-contact-dialog/member-area-contact-dialog.component';
import { RenameFileModalComponent } from '../../../shared-components/rename-file-modal/rename-file-modal.component';

interface AreaContentItem {
  kind: 'file' | 'link';
  id: number;
  title: string;
  createdAt: Date | null;
  position?: number | null;
  file?: UserAreaFileModel;
  link?: UserAreaLinkModel;
}
@Component({
  selector: 'app-member-area-details',
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss',
  standalone: false,
})
export class MemberAreaDetailsComponent implements OnInit {
  area: UserAreaModel;
  memberships: UserAreaMembershipModel[] = [];
  loading = true;
  managing = false;

  uploading = false;
  filesLoading = true;
  files: UserAreaFileModel[] = [];
  links: UserAreaLinkModel[] = [];

  showImagePreview = false;
  previewImageUrl: string | null = null;
  previewImageFileName = '';

  showUploadModal = false;
  activeModalTab: 'file' | 'link' = 'file';

  panelCollapsed = false;
  reorderMode = false;
  allContentItems: AreaContentItem[] = [];
  reorderContentItems: AreaContentItem[] = [];

  linkForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    url: new FormControl('', [Validators.required]),
  });

  roleOptions: SelectItem<UserAreaRole>[] = [
    { label: 'Mitglied', value: 'MEMBER' },
    { label: 'Admin', value: 'ADMIN' },
    { label: 'Eigentümer', value: 'OWNER', disabled: true },
  ];

  private headerBackgroundUrl: string | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private userAreaService: UserAreaService,
    private alertService: AlertService,
    public userService: UserService,
    private dialogService: DialogService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const areaIdParam = this.activatedRoute.snapshot.params['areaId'];
    if (!areaIdParam) return;
    const areaId = +atob(areaIdParam);
    this.loadArea(areaId);
  }

  get adminMembers(): UserAreaMembershipModel[] {
    return this.memberships.filter(m => m.role === 'ADMIN');
  }

  get ownerMember(): UserAreaMembershipModel | undefined {
    return this.memberships.find(m => m.role === 'OWNER');
  }

  get canContact(): boolean {
    return !!this.area?.hasContact;
  }

  onContactClick(): void {
    if (!this.area) return;

    const dialogRef = this.dialogService.open(
      MemberAreaContactDialogComponent,
      {
        width: '500px',
        contentStyle: { overflow: 'auto' },
        breakpoints: {
          '640px': '90vw',
        },
        data: {
          areaName: this.area.name,
        },
      }
    );

    dialogRef.onClose.pipe(take(1)).subscribe((message: string | null) => {
      if (!message) return;

      this.userAreaService
        .requestUserAreaContact(this.area.id, message)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert(
              'Deine Kontaktanfrage wurde an den Ansprechpartner gesendet.'
            );
          },
          error: err => {
            this.alertService.showErrorAlert(
              err?.message || 'Die Kontaktanfrage konnte nicht gesendet werden.'
            );
          },
        });
    });
  }

  get currentUserRole(): UserAreaRole {
    const uid = this.userService.currentUser$.value?.id;
    return (
      this.memberships.find(m => m.user_id === uid)?.role ||
      ('MEMBER' as UserAreaRole)
    );
  }

  canManage(): boolean {
    if (this.isGlobalAdmin) return true;
    return ['OWNER', 'ADMIN'].includes(this.currentUserRole);
  }

  get isGlobalAdmin(): boolean {
    return this.userService.currentUser$.value?.roleId === 1;
  }

  loadArea(areaId: number): void {
    this.loading = true;
    this.userAreaService
      .getArea(areaId)
      .pipe(take(1))
      .subscribe({
        next: a => {
          this.area = a;
          if (this.area?.image) {
            this.headerBackgroundUrl = URL.createObjectURL(this.area.image);
          } else {
            this.headerBackgroundUrl = null;
          }
          this.loading = false;
          this.loadMemberships();
          this.loadFiles();
          this.loadLinks();
        },
        error: error => {
          console.error('Error loading area', error);
          this.alertService.showErrorAlert(
            'Bereich konnte nicht geladen werden.'
          );
          this.loading = false;
        },
      });
  }

  loadMemberships(): void {
    this.userAreaService
      .getMemberships(this.area.id)
      .pipe(take(1))
      .subscribe({
        next: ms => {
          this.memberships = ms;
        },
        error: () =>
          this.alertService.showErrorAlert(
            'Mitglieder konnten nicht geladen werden.'
          ),
      });
  }

  removeMember(m: UserAreaMembershipModel, event: Event): void {
    event.stopPropagation();

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Mitglied entfernen',
        message:
          'Bist du sicher, dass du dieses Mitglied aus dem Bereich entfernen möchtest?',
        icon: 'fa-regular fa-user-xmark',
        iconColor: 'red',
        confirmButtonText: 'Entfernen',
        confirmButtonIcon: 'fa-regular fa-user-xmark',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.userAreaService
        .removeUserFromArea(this.area.id, m.user_id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Mitglied entfernt.');
            this.loadMemberships();
          },
          error: err => {
            this.alertService.showErrorAlert(
              err?.message || 'Mitglied konnte nicht entfernt werden.'
            );
          },
        });
    });
  }

  loadFiles(): void {
    this.filesLoading = true;
    this.userAreaService
      .getAreaFiles(this.area.id)
      .pipe(take(1))
      .subscribe({
        next: f => {
          this.files = f;
          this.filesLoading = false;
          this.buildContentItems();
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Dateien konnten nicht geladen werden.'
          );
          this.filesLoading = false;
        },
      });
  }

  loadLinks(): void {
    this.userAreaService
      .getAreaLinks(this.area.id)
      .pipe(take(1))
      .subscribe({
        next: l => {
          (this.links = l), this.buildContentItems();
        },
        error: () => console.error('Links load failed'),
      });
  }

  private buildContentItems(): void {
    const fileItems: AreaContentItem[] = this.files.map(f => ({
      kind: 'file',
      id: f.id,
      title: f.fileName,
      createdAt: f.uploadedAt ? new Date(f.uploadedAt) : null,
      position: (f as any).position ?? null,
      file: f,
    }));

    const linkItems: AreaContentItem[] = this.links.map(l => ({
      kind: 'link',
      id: l.id,
      title: l.title,
      createdAt: l.createdAt ? new Date(l.createdAt) : null,
      position: (l as any).position ?? null,
      link: l,
    }));

    const items = [...fileItems, ...linkItems];

    items.sort((a, b) => {
      const pa = a.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.position ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      const ta = a.createdAt?.getTime() ?? 0;
      const tb = b.createdAt?.getTime() ?? 0;
      return tb - ta;
    });

    this.allContentItems = items;
  }

  toggleReorderMode(): void {
    if (!this.reorderMode) {
      // enter reorder mode
      this.reorderContentItems = this.allContentItems.map((item, index) => ({
        ...item,
        position: index + 1,
      }));
      this.reorderMode = true;
    } else {
      // leave reorder mode: persist order
      const payload = this.reorderContentItems.map((item, index) => ({
        kind: item.kind,
        id: item.id,
        position: index + 1,
      }));

      this.userAreaService
        .reorderAreaContent(this.area.id, payload)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Reihenfolge gespeichert.');
            // update local state
            this.reorderMode = false;
            // update positions in local models
            this.reorderContentItems.forEach((item, index) => {
              item.position = index + 1;
            });
            this.allContentItems = [...this.reorderContentItems];
          },
          error: err => {
            this.alertService.showErrorAlert(
              err?.message || 'Reihenfolge konnte nicht gespeichert werden.'
            );
          },
        });
    }
  }

  onReorderContent(): void {
    this.reorderContentItems.forEach((item, index) => {
      item.position = index + 1;
    });
    this.reorderContentItems = [...this.reorderContentItems];
  }

  onContentRowClick(item: AreaContentItem): void {
    if (item.kind === 'file' && item.file) {
      // NEW: image files open in preview modal
      if (this.isImage(item.file.fileName)) {
        this.openImagePreview(item.file);
      } else {
        this.openFile(item.file);
      }
    } else if (item.kind === 'link' && item.link) {
      this.openLink(item.link);
    }
  }

  openImagePreview(file: UserAreaFileModel): void {
    this.userAreaService
      .getAreaFileBlob(file.id)
      .pipe(take(1))
      .subscribe({
        next: blob => {
          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
          }
          this.previewImageUrl = URL.createObjectURL(blob);
          this.previewImageFileName = file.fileName;
          this.showImagePreview = true;
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Bild konnte nicht geöffnet werden.'
          );
        },
      });
  }

  closeImagePreview(): void {
    if (this.previewImageUrl) {
      URL.revokeObjectURL(this.previewImageUrl);
      this.previewImageUrl = null;
    }
    this.showImagePreview = false;
  }

  openContentUploadModal(): void {
    this.showUploadModal = true;
    this.activeModalTab = 'file';
    this.linkForm.reset();
  }

  openInviteModal(): void {
    const dialogRef = this.dialogService.open(InviteMemberAreaComponent, {
      width: '90vw',
      style: { maxWidth: '600px' },
      data: {
        areaId: this.area.id,
      },
    });
    dialogRef.onClose.subscribe((success: boolean) => {
      if (success) {
        this.loadMemberships();
      }
    });
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
  }

  switchModalTab(tab: 'file' | 'link'): void {
    this.activeModalTab = tab;
  }

  updateRole(m: UserAreaMembershipModel, role: UserAreaRole): void {
    this.userAreaService
      .updateUserRole(this.area.id, m.user_id, role)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Rolle aktualisiert.');
          this.loadMemberships();
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Rolle konnte nicht aktualisiert werden.'
          );
        },
      });
  }

  onFileSelected(event: Event): void {
    if (!this.canManage()) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    this.uploading = true;
    this.userAreaService
      .uploadAreaFile(this.area.id, file)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Datei hochgeladen.');
          this.uploading = false;
          this.closeUploadModal();
          this.loadFiles();
        },
        error: () => {
          this.alertService.showErrorAlert('Upload fehlgeschlagen.');
          this.uploading = false;
        },
      });
  }

  get documents(): UserAreaFileModel[] {
    return this.files.filter(f => !this.isImage(f.fileName));
  }

  get images(): UserAreaFileModel[] {
    return this.files.filter(f => this.isImage(f.fileName));
  }

  isImage(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
  }

  getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'assets/icons/pdf-icon.svg';
      case 'doc':
      case 'docx':
        return 'assets/icons/word-icon.svg';
      case 'xls':
      case 'xlsx':
        return 'assets/icons/excel-icon.svg';
      case 'ppt':
      case 'pptx':
        return 'assets/icons/ppt-icon.svg';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'webp':
        return 'assets/icons/img-icon.svg';
      default:
        return 'assets/icons/doc-icon.svg';
    }
  }

  openFile(file: UserAreaFileModel): void {
    this.userAreaService
      .getAreaFileBlob(file.id)
      .pipe(take(1))
      .subscribe({
        next: blob => {
          // Create a blob URL
          const blobUrl = URL.createObjectURL(blob);
          // Open in new window
          const newWindow = window.open(blobUrl, '_blank');

          // Clean up the blob URL after the window loads
          if (newWindow) {
            newWindow.onload = () => {
              URL.revokeObjectURL(blobUrl);
            };
          }
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Datei konnte nicht geöffnet werden.'
          );
        },
      });
  }

  deleteFile(file: UserAreaFileModel, event: Event): void {
    event.stopPropagation();

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Datei löschen',
        message:
          'Bist du sicher, dass du diese Datei löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userAreaService
          .deleteAreaFile(file.id)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.alertService.showSuccessAlert('Datei gelöscht.');
              this.loadFiles();
            },
            error: () =>
              this.alertService.showErrorAlert('Löschen fehlgeschlagen.'),
          });
      }
    });
  }

  renameFile(file: UserAreaFileModel, event: Event): void {
    event.stopPropagation();

    const dialogRef = this.dialogService.open(RenameFileModalComponent, {
      width: '90vw',
      style: { maxWidth: '500px' },
      data: {
        currentFileName: file.fileName,
      },
    });

    dialogRef.onClose.subscribe((newName: string | null) => {
      if (!newName || newName.trim() === '') return;

      const originalExt = file.fileName.split('.').pop() || '';
      const newNameWithoutExt = newName.replace(/\.[^.]+$/, '');
      const finalName = originalExt
        ? `${newNameWithoutExt}.${originalExt}`
        : newNameWithoutExt;

      this.userAreaService
        .renameAreaFile(file.id, finalName)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Datei umbenannt.');
            this.loadFiles();
          },
          error: err => {
            this.alertService.showErrorAlert(
              err?.message || 'Umbenennen fehlgeschlagen.'
            );
          },
        });
    });
  }

  // --- Link Actions ---
  onSaveLink(): void {
    if (this.linkForm.invalid) return;
    const { title, url } = this.linkForm.value;
    this.userAreaService
      .createAreaLink(this.area.id, title!, url!)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Link gespeichert.');
          this.closeUploadModal();
          this.loadLinks();
        },
        error: () =>
          this.alertService.showErrorAlert('Speichern fehlgeschlagen.'),
      });
  }

  deleteLink(link: UserAreaLinkModel, event: Event): void {
    event.stopPropagation();

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Link löschen',
        message:
          'Bist du sicher, dass du diesen Link löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userAreaService
          .deleteAreaLink(link.id)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.alertService.showSuccessAlert('Link gelöscht.');
              this.loadLinks();
            },
            error: () =>
              this.alertService.showErrorAlert('Löschen fehlgeschlagen.'),
          });
      }
    });
  }

  openLink(link: UserAreaLinkModel): void {
    window.open(link.url, '_blank');
  }

  public getIconForFile(file: UserAreaFileModel): string {
    const name = file.fileName.toLowerCase();
    const type = (file.fileName.split('.').pop() || '').toLowerCase();
    const endsWith = (...exts: string[]) => exts.some(e => name.endsWith(e));
    if (type.includes('pdf') || endsWith('.pdf')) return 'fa-file-pdf';
    if (type.includes('spreadsheet') || endsWith('.xls', '.xlsx', '.csv'))
      return 'fa-file-excel';
    if (type.includes('presentation') || endsWith('.ppt', '.pptx'))
      return 'fa-file-powerpoint';
    if (type.includes('word') || endsWith('.doc', '.docx'))
      return 'fa-file-word';
    if (
      type.startsWith('image/') ||
      endsWith('.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg')
    )
      return 'fa-file-image';
    if (type.includes('zip') || endsWith('.zip', '.rar', '.7z'))
      return 'fa-file-zipper';
    if (type.includes('text') || endsWith('.txt', '.md', '.rtf'))
      return 'fa-file-lines';
    return 'fa-file';
  }

  public getIconForLink(): string {
    return 'fa-link';
  }

  openEditModal(): void {
    if (this.isGlobalAdmin) {
      this.router.navigate(
        ['/administration/member-areas/edit', btoa(String(this.area.id))],
        {}
      );
      return;
    }
    const dialogRef = this.dialogService.open(EditMemberAreaComponent, {
      width: '90vw',
      style: { maxWidth: '600px' },
      data: {
        area: this.area,
      },
    });
    dialogRef.onClose.subscribe((success: boolean) => {
      if (success) {
        this.loadArea(this.area.id); // Reload to get updated data
      }
    });
  }

  copyCodeToClipboard(): void {
    if (!this.area.code) return;

    navigator.clipboard.writeText(this.area.code).then(
      () => {
        this.alertService.showSuccessAlert('Code in Zwischenablage kopiert.');
      },
      () => {
        this.alertService.showErrorAlert('Kopieren fehlgeschlagen.');
      }
    );
  }

  get headerBackgroundImage(): string {
    if (!this.area.image) return '';
    return URL.createObjectURL(this.area.image);
  }
}
